// src/components/module/almacen/InventariosImportModal/InventariosImportModal.tsx
// Rediseño corporativo estilo Notion/Linear
import React, { useState, useContext } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  LinearProgress,
  Typography,
  Box,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  alpha,
} from "@mui/material";
import {
  CloudUpload as UploadIcon, Close as CloseIcon, Download as DownloadIcon,
  CheckCircle, Error as ErrorIcon, Description,
} from "@mui/icons-material";
import { useInventariosApi } from "../../../../services/api/inventariosApi";
import { DarkModeContext } from "../../../../context/darkModeContext";
const P = {
  light: { bg: "#FFFFFF", bgS: "#F7F7F5", bgT: "#F1F1EF", text: "#37352F", textS: "#6B6B6B", textT: "#9B9A97", border: "#E9E9E7", borderH: "#D3D3D0", accent: "#2383E2", accentBg: "#E8F0FE", success: "#0F7B6C", successBg: "#DBEDDB", error: "#E03E3E", errorBg: "#FBE4E4", hover: "rgba(55,53,47,0.04)" },
  dark: { bg: "#191919", bgS: "#202020", bgT: "#2B2B2B", text: "#E8E8E6", textS: "#9B9A97", textT: "#6B6B6B", border: "#333333", borderH: "#444444", accent: "#529CCA", accentBg: "#143A52", success: "#4DAB9A", successBg: "#1A2F2A", error: "#D44C47", errorBg: "#3D2020", hover: "rgba(255,255,255,0.04)" },
};
interface ImportResult { success: any[]; errors: { articulo: string; error: string }[]; }
interface Props { isOpen: boolean; onClose: () => void; onImportComplete: () => void; }
const InventariosImportModal: React.FC<Props> = ({ isOpen, onClose, onImportComplete }) => {
  const { downloadTemplate, importExcel } = useInventariosApi();
  const { darkMode } = useContext(DarkModeContext);
  const p = darkMode ? P.dark : P.light;
  const [file, setFile] = useState<File | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<ImportResult | null>(null);
  const handleClose = () => { setFile(null); setResult(null); onClose(); };
  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => { if (e.target.files?.[0]) { setFile(e.target.files[0]); setResult(null); } };
  const handleUpload = async () => {
    if (!file) return;
    setIsLoading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await importExcel(fd); setResult(r);
      if (r.success.length > 0) onImportComplete();
    } catch (e) { console.error(e); } finally { setIsLoading(false); }
  };
  return (
    <Dialog open={isOpen} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: "14px", border: `1px solid ${p.border}`, bgcolor: p.bg, overflow: "hidden" } }}>
        <Box sx={{ px: 3, py: 2.5, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${p.border}` }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: p.accentBg, display: "flex", alignItems: "center", justifyContent: "center", color: p.accent }}><UploadIcon sx={{ fontSize: 20 }} /></Box>
            <Box>
              <Typography sx={{ fontSize: "1.05rem", fontWeight: 700, color: p.text }}>Importar Inventarios</Typography>
              <Typography sx={{ fontSize: "0.75rem", color: p.textT }}>Carga masiva desde archivo Excel</Typography>
            </Box>
          </Stack>
          <IconButton onClick={handleClose} sx={{ width: 32, height: 32, borderRadius: "8px", color: p.textT, "&:hover": { bgcolor: p.bgT } }}><CloseIcon sx={{ fontSize: 18 }} /></IconButton>
        </Box>
        <DialogContent sx={{ p: 3, bgcolor: p.bgS }}>
          <Box sx={{ p: 2.5, mb: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>Paso 1</Typography>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: p.text, mb: 0.5 }}>Descarga la plantilla</Typography>
            <Typography sx={{ fontSize: "0.78rem", color: p.textT, mb: 2 }}>Complétala con los datos de tus inventarios.</Typography>
            <Button onClick={() => downloadTemplate()} startIcon={<DownloadIcon sx={{ fontSize: 16 }} />}
              sx={{ color: p.accent, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", border: `1px solid ${p.border}`, px: 2, "&:hover": { bgcolor: p.hover, borderColor: p.accent } }}>
              Descargar Plantilla
            </Button>
          </Box>
          <Box sx={{ p: 2.5, mb: 2.5, borderRadius: "10px", border: `1px solid ${p.border}`, bgcolor: p.bg }}>
            <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: p.textT, textTransform: "uppercase", letterSpacing: "0.04em", mb: 0.5 }}>Paso 2</Typography>
            <Typography sx={{ fontSize: "0.88rem", fontWeight: 600, color: p.text, mb: 0.5 }}>Sube tu archivo</Typography>
            <Typography sx={{ fontSize: "0.78rem", color: p.textT, mb: 2 }}>Selecciona el archivo Excel completado.</Typography>
            <input type="file" accept=".xlsx,.xls" onChange={handleFile} style={{ display: "none" }} id="file-upload-inventarios" />
            <label htmlFor="file-upload-inventarios">
              <Button component="span" variant="contained" disableElevation startIcon={<UploadIcon sx={{ fontSize: 16 }} />} disabled={isLoading}
                sx={{ bgcolor: p.accent, color: "#FFF", fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" } }}>
                Seleccionar Archivo
              </Button>
            </label>
            {file && (
              <Box sx={{ mt: 2, p: 2, borderRadius: "8px", bgcolor: p.bgS, border: `1px dashed ${p.accent}` }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Description sx={{ fontSize: 16, color: p.accent }} />
                  <Box><Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: p.text }}>{file.name}</Typography><Typography sx={{ fontSize: "0.72rem", color: p.textT }}>{(file.size / 1024).toFixed(1)} KB</Typography></Box>
                </Stack>
              </Box>
            )}
          </Box>
          {isLoading && <Box sx={{ mb: 2 }}><Typography sx={{ fontSize: "0.78rem", color: p.textS, mb: 0.5 }}>Procesando...</Typography><LinearProgress sx={{ borderRadius: 2, bgcolor: p.bgT, "& .MuiLinearProgress-bar": { bgcolor: p.accent } }} /></Box>}
          {result && (
            <Box>
              {result.success.length > 0 && (
                <Box sx={{ p: 2.5, mb: 2, borderRadius: "10px", border: `1px solid ${p.success}`, bgcolor: p.successBg }}>
                  <Stack direction="row" alignItems="center" spacing={1}><CheckCircle sx={{ fontSize: 16, color: p.success }} /><Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: p.success }}>{result.success.length} inventario(s) importado(s)</Typography></Stack>
                </Box>
              )}
              {result.errors.length > 0 && (
                <Box sx={{ p: 2.5, borderRadius: "10px", border: `1px solid ${p.error}`, bgcolor: p.errorBg }}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}><ErrorIcon sx={{ fontSize: 16, color: p.error }} /><Typography sx={{ fontSize: "0.84rem", fontWeight: 600, color: p.error }}>{result.errors.length} error(es)</Typography></Stack>
                  <List dense sx={{ p: 0 }}>
                    {result.errors.slice(0, 5).map((err, i) => (
                      <React.Fragment key={i}>
                        <ListItem sx={{ px: 0 }}><ListItemText primary={`Artículo: ${err.articulo}`} secondary={err.error} primaryTypographyProps={{ fontSize: "0.82rem", fontWeight: 600, color: p.text }} secondaryTypographyProps={{ fontSize: "0.75rem", color: p.textT }} /></ListItem>
                        {i < Math.min(4, result.errors.length - 1) && <Divider sx={{ borderColor: alpha(p.error, 0.2) }} />}
                      </React.Fragment>
                    ))}
                  </List>
                  {result.errors.length > 5 && <Typography sx={{ fontSize: "0.72rem", color: p.textT, mt: 1 }}>...y {result.errors.length - 5} más</Typography>}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <Box sx={{ px: 3, py: 2, borderTop: `1px solid ${p.border}`, bgcolor: p.bg, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button onClick={handleClose} sx={{ color: p.textS, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: p.hover } }}>Cerrar</Button>
          <Button onClick={handleUpload} variant="contained" disableElevation disabled={!file || isLoading}
            sx={{ bgcolor: p.accent, color: "#FFF", px: 3, fontSize: "0.82rem", fontWeight: 600, borderRadius: "8px", "&:hover": { bgcolor: darkMode ? "#6BAFDB" : "#1B6FC2" }, "&.Mui-disabled": { bgcolor: alpha(p.accent, 0.15) } }}>
            {isLoading ? "Importando..." : "Importar"}
          </Button>
        </Box>
      </Dialog>
  );
};
export default InventariosImportModal;
